import type { CSSProperties, ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { money } from '../../lib/format';
import { artProductId, defaultTacticRole, deliveryKind, pricedPlan, productFamily, productLabel, tacticFor } from '../../lib/plan';
import { useBindings } from '../../state/BindingsContext';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { productIconAsset } from './assets';
import { Frame, focalPointStyle } from './primitives';

/**
 * Rounds a set of non-negative weights to whole percentages that sum to
 * EXACTLY 100 via the largest-remainder method (design.md V15 media_mix
 * derivation: "`$` and rounded `%` summing to 100"). `src/util` isn't
 * importable from web (per the brief), so this is a small local copy of the
 * same well-known algorithm rather than a shared import. Exported so the
 * behavior is independently testable. Unchanged by the docs/momentum-v3-
 * slides.md media_mix rebuild — only the presentation below changed.
 */
export function largestRemainderPercentages(weights: number[]): number[] {
  if (weights.length === 0) return [];
  const total = weights.reduce((sum, weight) => sum + Math.max(weight, 0), 0);
  if (total <= 0) return weights.map(() => 0);
  const raw = weights.map((weight) => (Math.max(weight, 0) / total) * 100);
  const result = raw.map((value) => Math.floor(value));
  let remainder = 100 - result.reduce((sum, value) => sum + value, 0);
  const byFractionDesc = raw
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac);
  for (let i = 0; i < byFractionDesc.length && remainder > 0; i += 1) {
    const entry = byFractionDesc[i];
    if (!entry) continue;
    result[entry.index] = (result[entry.index] ?? 0) + 1;
    remainder -= 1;
  }
  return result;
}

/** A muted tone "related to" the theme accent — the primary channel gets the
 * accent solid, every other channel a progressively fainter mix of it
 * against a dark neutral, so segments read as one family rather than four
 * unrelated hues (audit §8.8/§8.9: "muted related tones derived from it").
 * `color-mix` is the one CSS feature this leans on beyond what the rest of
 * v3 uses — it renders identically in the Chromium build that prints/exports
 * decks, and degrades to the last valid color (the accent) in anything that
 * doesn't understand it, never to invisible. */
const MIX_WEIGHTS = [100, 62, 46, 34, 26, 20, 16];
function toneForIndex(index: number): string {
  const weight = MIX_WEIGHTS[Math.min(index, MIX_WEIGHTS.length - 1)];
  return index === 0 ? 'var(--mv3-mint)' : `color-mix(in srgb, var(--mv3-mint) ${weight}%, #454b51)`;
}

/** A neutral channel glyph for products the icon kit has no art for
 * (audit §8.9: "icon ... else a neutral glyph"), never a blank tile. */
function NeutralChannelGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" />
    </svg>
  );
}

/** Four small, purely decorative outline glyphs for the bottom value strip
 * (audit §8.11 example: target / bars / people / trend) — presentational
 * only, not tied to the authored copy's meaning. */
const STRIP_GLYPHS: readonly (() => ReactElement)[] = [
  () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </svg>
  ),
  () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <rect x="4" y="13" width="3.4" height="7" rx="0.8" fill="currentColor" />
      <rect x="10.3" y="9" width="3.4" height="11" rx="0.8" fill="currentColor" />
      <rect x="16.6" y="5" width="3.4" height="15" rx="0.8" fill="currentColor" />
    </svg>
  ),
  () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        d="M12 21s-6.5-5.2-6.5-10.2A6.5 6.5 0 0 1 12 4a6.5 6.5 0 0 1 6.5 6.8C18.5 15.8 12 21 12 21Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="12" cy="10.6" r="2.2" fill="currentColor" />
    </svg>
  ),
  () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path d="M4 16 10 10 14 13 20 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 6h5v5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
];

interface MixDonutSlice {
  id: string;
  value: number;
  pct: number;
  color: string;
}

/** angleDeg measured clockwise from 12 o'clock, matching the stroke-dash
 * convention below (each circle's stroke starts at the top and winds
 * clockwise). Radius is measured from the donut's own center. */
function polarPoint(cx: number, cy: number, radius: number, angleDeg: number): { x: number; y: number } {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
}

/**
 * media_mix's own donut (audit §8.8): outside percentage labels with short
 * leader lines for up to 5 channels, collision-resolved by nudging labels
 * vertically rather than letting them overlap or truncating either one.
 * Kept local per the stream 2 brief ("a local copy of the Donut inside your
 * page file") rather than in primitives.tsx, which the shared plain-legend
 * `Donut` still serves for every other v3 slide.
 */
function MixDonut({
  slices,
  size,
  strokeWidth,
  centerValue,
  showLabels,
}: {
  slices: MixDonutSlice[];
  size: number;
  strokeWidth: number;
  centerValue: string;
  showLabels: boolean;
}): ReactElement {
  const total = slices.reduce((sum, slice) => sum + Math.max(slice.value, 0), 0) || 1;
  const cx = size / 2;
  const cy = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let cursor = 0;
  const arcs = slices.map((slice) => {
    const fraction = Math.max(slice.value, 0) / total;
    const length = fraction * circumference;
    const dashOffset = -cursor;
    const startAngle = (cursor / circumference) * 360;
    cursor += length;
    const endAngle = (cursor / circumference) * 360;
    return { ...slice, length, dashOffset, midAngle: (startAngle + endAngle) / 2 };
  });

  const labelRadius = radius + strokeWidth / 2 + 34;
  const leaderRadius = radius + strokeWidth / 2 + 6;
  const labels = arcs
    .map((arc) => {
      const outer = polarPoint(cx, cy, labelRadius, arc.midAngle);
      const inner = polarPoint(cx, cy, leaderRadius, arc.midAngle);
      return { id: arc.id, pct: arc.pct, x: outer.x, y: outer.y, innerX: inner.x, innerY: inner.y, rightSide: outer.x >= cx };
    })
    .sort((a, b) => a.y - b.y);
  // Collision resolution (audit §8.8): if two labels land too close
  // vertically, push the lower one down rather than overlap or drop either.
  const MIN_GAP = 24;
  for (let i = 1; i < labels.length; i += 1) {
    const prev = labels[i - 1];
    const cur = labels[i];
    if (prev && cur && cur.y - prev.y < MIN_GAP) cur.y = prev.y + MIN_GAP;
  }

  return (
    <div className="mv3-mm-donut" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-hidden="true" overflow="visible">
        <g transform={`rotate(-90 ${cx} ${cy})`}>
          {arcs.map((arc) => (
            <circle
              key={arc.id}
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${arc.length} ${circumference - arc.length}`}
              strokeDashoffset={arc.dashOffset}
            />
          ))}
        </g>
        {showLabels
          ? labels.map((label) => (
              <g key={label.id}>
                <line x1={label.innerX} y1={label.innerY} x2={label.x} y2={label.y} stroke="rgba(255,255,255,0.35)" strokeWidth={1} />
                <text
                  x={label.x + (label.rightSide ? 8 : -8)}
                  y={label.y}
                  textAnchor={label.rightSide ? 'start' : 'end'}
                  dominantBaseline="middle"
                  className="mv3-mm-donut-pct"
                >
                  {label.pct}%
                </text>
              </g>
            ))
          : null}
      </svg>
      <div className="mv3-mm-donut-center">
        <span className="mv3-mm-donut-eyebrow">Total investment</span>
        <span className="mv3-mm-donut-value">{centerValue}</span>
        <span className="mv3-mm-donut-sub">/ month</span>
      </div>
    </div>
  );
}

/** Card height (px) by channel count (audit §8.10) — 2 channels get a taller,
 * vertically centred pair; past 5, compact mode keeps every channel but
 * drops the role line rather than shrinking type past comfortable reading
 * size or clipping the last card. */
function cardHeightFor(count: number): number {
  if (count <= 2) return 156;
  if (count === 3) return 116;
  if (count === 4) return 98;
  if (count === 5) return 78;
  return 60;
}

/**
 * media_mix (docs/momentum-v3-slides.md, MAJOR REBUILD): dark, image-led
 * investment slide. Data path unchanged from the original v3 media_mix
 * (and momentum_v2's before it): the active tier's `PricedLine`s sorted by
 * `monthlyBudget` desc, largest-remainder shares, subtotal in the donut
 * centre — never a number the renderer itself computes. Everything below
 * `pricedPlan`/`largestRemainderPercentages` is presentation.
 */
export function MediaMixPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const resolve = useBindings();
  const plan = pricedPlan(proposal);
  const { productName } = useContentLibrary();
  const labelFor = (productId: string) => productName(productId) ?? productLabel(proposal, productId);
  const lines = [...(plan?.lines ?? [])].sort((a, b) => b.monthlyBudget - a.monthlyBudget);
  const percentages = largestRemainderPercentages(lines.map((line) => line.monthlyBudget));
  const channels = lines.map((line, index) => {
    const kind = deliveryKind(line);
    const family = productFamily(proposal, line.productId);
    // `tactic.role` is agent-authored (proposal.strategy.tactics[]) and can
    // carry a live {{binding}} - kept raw here, resolved where it renders.
    const role = tacticFor(proposal, line.productId)?.role || defaultTacticRole(line.productId, kind, family);
    return {
      id: line.productId,
      index,
      name: labelFor(line.productId),
      role,
      icon: productIconAsset(artProductId(proposal, line.productId)),
      budget: line.monthlyBudget,
      pct: percentages[index] ?? 0,
      color: toneForIndex(index),
    };
  });
  const compact = channels.length > 5;
  const cardHeight = cardHeightFor(channels.length);

  // media_mix's own field per the v3 manifest: `content.body` is the
  // allocation rationale (assembly seeds it from `mediaPlan
  // .recommendationRationale`); the strategy summary only fills the slide
  // until the rep or agent writes their own, same precedence every other
  // v3 statement slide uses.
  const rationale = section.content.body || proposal.strategy.summary || undefined;
  const fields = section.content.fields ?? {};

  // `fields.contextLabel` is stored `|`-joined (manifest: "`|`-separated
  // lines, max 4") but edited and displayed as real lines — `\n` in the
  // textarea while editing, `white-space: pre-line` while idle — rather
  // than asking the rep to type a literal `|`.
  const contextDisplay = (fields.contextLabel ?? '')
    .split('|')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 4)
    .join('\n');

  const stripItems = ([1, 2, 3, 4] as const)
    .map((n) => ({
      n,
      title: (fields[`strip${n}Title`] ?? '').trim(),
      body: fields[`strip${n}Body`] ?? '',
    }))
    .filter((item) => item.title.length > 0);
  const showStrip = stripItems.length >= 3;

  const imageUrl = section.content.imageUrl;
  const focalStyle = focalPointStyle(section) ?? { objectPosition: '75% 50%' };

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-mediamix-root"
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-mm-frame">
          <div className="mv3-mm-photo">
            {imageUrl ? <img src={imageUrl} alt="" className="mv3-mm-photo-image" style={focalStyle} aria-hidden="true" /> : null}
            <div className="mv3-mm-scrim" aria-hidden="true" />
            {!readOnly ? (
              <ImageSlot
                url={imageUrl}
                alt="Media mix background"
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { imageUrl: next })}
                className="mv3-mm-image-affordance"
                hideImage
                proposalId={proposal.proposalId}
                defaultQuery={proposal.advertiser.industry ?? section.title}
              />
            ) : null}
          </div>

          <div className="mv3-mm-content">
            <div className="mv3-mm-header">
              <div className="mv3-mm-header-main">
                <div className="mv3-mm-eyebrow-row">
                  <span className="mv3-mm-eyebrow">Investment plan</span>
                  <i className="mv3-mm-eyebrow-rule" aria-hidden="true" />
                </div>
                <h1 className="mv3-mm-title">
                  Media <span className="mv3-mm-title-accent">mix</span>
                </h1>
                <div className="mv3-mm-rationale">
                  <EditableText
                    value={rationale}
                    field="body"
                    placeholder="Summarize the allocation in one sentence"
                    readOnly={readOnly}
                    onSave={(next) => actions.saveContent(section, { body: next })}
                  />
                </div>
              </div>
              {contextDisplay || !readOnly ? (
                <div className="mv3-mm-context">
                  <EditableText
                    value={contextDisplay}
                    field="fields.contextLabel"
                    placeholder="Optional context, one short line per line"
                    readOnly={readOnly}
                    onSave={(next) =>
                      actions.saveContent(section, {
                        fields: {
                          ...fields,
                          contextLabel: next
                            .split('\n')
                            .map((line) => line.trim())
                            .filter(Boolean)
                            .slice(0, 4)
                            .join('|'),
                        },
                      })
                    }
                  />
                </div>
              ) : null}
            </div>

            {channels.length > 0 ? (
              <>
                <div className="mv3-mm-main">
                  <div className="mv3-mm-donut-wrap">
                    <MixDonut
                      slices={channels.map((c) => ({ id: c.id, value: c.budget, pct: c.pct, color: c.color }))}
                      size={380}
                      strokeWidth={78}
                      centerValue={money(plan?.subtotalMonthly ?? null) ?? '—'}
                      showLabels={!compact}
                    />
                  </div>
                  <ul className={`mv3-mm-cards${channels.length <= 2 ? ' mv3-mm-cards-centered' : ''}`}>
                    {channels.map((c) => (
                      <li
                        key={c.id}
                        className={`mv3-mm-card${compact ? ' mv3-mm-card-compact' : ''}`}
                        style={{ height: cardHeight, '--mm-card-color': c.color } as CSSProperties}
                      >
                        <span className="mv3-mm-card-num" style={c.index === 0 ? { color: 'var(--mv3-mint)' } : undefined}>
                          {String(c.index + 1).padStart(2, '0')}
                        </span>
                        <span className="mv3-mm-card-icon">{c.icon ? <img src={c.icon} alt="" aria-hidden="true" /> : <NeutralChannelGlyph />}</span>
                        <span className="mv3-mm-card-text">
                          <span className="mv3-mm-card-name">{c.name}</span>
                          {!compact ? <span className="mv3-mm-card-role">{resolve(c.role)}</span> : null}
                        </span>
                        <span className="mv3-mm-card-money">
                          <span className="mv3-mm-card-budget">{money(c.budget) ?? '—'}</span>
                          <span className="mv3-mm-card-pct">{c.pct}%</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {showStrip ? (
                  <div className="mv3-mm-strip">
                    {stripItems.map((item, i) => {
                      const Glyph = STRIP_GLYPHS[i % STRIP_GLYPHS.length] ?? STRIP_GLYPHS[0]!;
                      return (
                        <div className="mv3-mm-strip-item" key={item.n}>
                          <span className="mv3-mm-strip-icon">
                            <Glyph />
                          </span>
                          <span className="mv3-mm-strip-text">
                            <span className="mv3-mm-strip-title">
                              <EditableText
                                value={item.title}
                                field={`fields.strip${item.n}Title`}
                                placeholder="Short title"
                                readOnly={readOnly}
                                onSave={(next) => actions.saveContent(section, { fields: { ...fields, [`strip${item.n}Title`]: next } })}
                              />
                            </span>
                            <span className="mv3-mm-strip-body">
                              <EditableText
                                value={item.body}
                                field={`fields.strip${item.n}Body`}
                                placeholder="Short supporting line"
                                readOnly={readOnly}
                                onSave={(next) => actions.saveContent(section, { fields: { ...fields, [`strip${item.n}Body`]: next } })}
                              />
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </>
            ) : (
              <p className="mv3-mm-empty">No channels in the current plan.</p>
            )}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
